/**
 * Importar extracto (móvil): lee el archivo elegido con el lector del
 * servidor (`transactions-parse-file`, que corre en el propio móvil) y lo
 * deja listo para comprobar. Un CSV se prueba con los separadores habituales
 * y, si sale con «�», otra vez en Windows-1252 (los bancos españoles lo usan
 * mucho).
 */
import { send } from '@actual-app/core/platform/client/connection';

import {
  movimientosEstructurados,
  tieneCaracteresRotos,
  tipoArchivo,
} from './importar';
import type { MovimientoEstructurado } from './importar';
import { elegirSeparador, leerColumnas } from './leerExtracto';
import type { Lectura, MovimientoLeido } from './leerExtracto';

export type ErrorLectura =
  /** Extensión que no se sabe leer. */
  | 'formato'
  /** Excel 97-2003 binario: guardarlo como .xlsx o CSV. */
  | 'excel-antiguo'
  /** Se leyó, pero no hay movimientos. */
  | 'vacio'
  /** El lector falló. */
  | 'ilegible';

export type ArchivoLeido =
  | { tipo: 'tabla'; filas: string[][]; lectura: Lectura | null }
  | {
      tipo: 'estructurado';
      movimientos: MovimientoLeido[];
      /** Saldo final del extracto en euros (Norma 43), si lo trae. */
      saldoFinal: number | null;
    }
  | { tipo: 'error'; error: ErrorLectura };

type Resultado = {
  errors?: Array<{ message: string; internal?: string }>;
  transactions?: unknown[];
  closingBalance?: number | null;
};

async function parsear(
  ruta: string,
  options: Record<string, unknown>,
): Promise<Resultado> {
  return (await send('transactions-parse-file', {
    filepath: ruta,
    options,
  })) as Resultado;
}

function aFilas(transactions: unknown[] = []): string[][] {
  return transactions.filter(Array.isArray).map(fila => fila.map(String));
}

function errorDe(res: Resultado): ErrorLectura | null {
  const error = res.errors?.[0];
  if (!error) {
    return null;
  }
  if (error.internal === 'old-excel') {
    return 'excel-antiguo';
  }
  return error.message === 'Invalid file type' ? 'formato' : 'ilegible';
}

async function leerTabla(ruta: string): Promise<ArchivoLeido> {
  const esCsv = /\.(csv|tsv)$/i.test(ruta);
  if (!esCsv) {
    const res = await parsear(ruta, { hasHeaderRow: false });
    const error = errorDe(res);
    if (error) {
      return { tipo: 'error', error };
    }
    const filas = aFilas(res.transactions);
    return { tipo: 'tabla', filas, lectura: leerColumnas(filas) };
  }

  const porSeparador: Record<string, string[][]> = {};
  for (const separador of [';', ',', '\t']) {
    let res = await parsear(ruta, {
      hasHeaderRow: false,
      delimiter: separador,
    });
    if (!res.errors?.length && tieneCaracteresRotos(aFilas(res.transactions))) {
      res = await parsear(ruta, {
        hasHeaderRow: false,
        delimiter: separador,
        encoding: 'windows-1252',
      });
    }
    if (!res.errors?.length) {
      porSeparador[separador] = aFilas(res.transactions);
    }
  }
  if (Object.keys(porSeparador).length === 0) {
    return { tipo: 'error', error: 'ilegible' };
  }
  const filas = porSeparador[elegirSeparador(porSeparador)];
  return { tipo: 'tabla', filas, lectura: leerColumnas(filas) };
}

export async function leerArchivo(ruta: string): Promise<ArchivoLeido> {
  const tipo = tipoArchivo(ruta);
  if (tipo == null) {
    return { tipo: 'error', error: 'formato' };
  }
  try {
    if (tipo === 'tabla') {
      const leido = await leerTabla(ruta);
      if (leido.tipo === 'tabla' && leido.filas.length === 0) {
        return { tipo: 'error', error: 'vacio' };
      }
      return leido;
    }
    const res = await parsear(ruta, { importNotes: false });
    const error = errorDe(res);
    if (error) {
      return { tipo: 'error', error };
    }
    const movimientos = movimientosEstructurados(
      (res.transactions ?? []) as MovimientoEstructurado[],
    );
    if (movimientos.length === 0) {
      return { tipo: 'error', error: 'vacio' };
    }
    return {
      tipo: 'estructurado',
      movimientos,
      saldoFinal: res.closingBalance ?? null,
    };
  } catch {
    return { tipo: 'error', error: 'ilegible' };
  }
}
